import { alpha } from "@mui/material/styles";
import { ChartData, ChartDataset } from "chart.js";
import { t } from "i18next";
import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import {
  getLegendLabels,
  getTicks,
  getTooltip,
  wrapLabel,
} from "../../utils/chartjsTheming";
import { getColorForCosts } from "./CostColors.ts";
import { StageEvData } from "./EvAnalyticsUtility.ts";

// A bar in the EV chart. `label` is pre-translated (and wrapped) by EvAnalytics
// and may describe a single stage or a packed range of stages.
export type EvChartDataOfStage = Omit<StageEvData, "stage"> & {
  label: string;
};

/**
 * How much of its colour the bar of an expected value takes: it is the large
 * area of the chart, and the page shows through the rest of it.
 */
const EXPECTED_VALUE_FILL_ALPHA = 0.56;

/** How the legend draws the series that are bars; a line shows its marker. */
const LEGEND_BOX = "rectRounded";

export const EvChart = ({
  stages: data,
  netResult,
}: {
  stages: EvChartDataOfStage[];
  netResult?: number;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();

  // Define colors and point styles
  const opponentColors = [
    theme.palette.primary.main,
    theme.palette.info.dark,
    theme.palette.warning.dark,
  ];
  const pointStyles: ("triangle" | "star" | "circle")[] = [
    "triangle",
    "star",
    "circle",
  ];

  const baselineMidpoints: ChartDataset<"line" | "bar", number[]>[] =
    useMemo(() => {
      const opponentIds = new Set<string>();
      data.forEach((stage) => {
        if (stage.settlementMidpoints) {
          Object.keys(stage.settlementMidpoints).forEach((id) =>
            opponentIds.add(id),
          );
        }
      });

      if (opponentIds.size === 0) return [];

      return Array.from(opponentIds).map((opponentId, index) => {
        const label =
          data.find((stage) => stage.settlementMidpoints?.[opponentId])
            ?.settlementMidpoints[opponentId].label || opponentId;

        return {
          label,
          data: data.map(
            (stage) => stage.settlementMidpoints?.[opponentId]?.midPoint ?? NaN,
          ),
          type: "line",
          borderColor: opponentColors[index % opponentColors.length],
          backgroundColor: opponentColors[index % opponentColors.length] + "70",
          borderWidth: 2,
          pointStyle: pointStyles[index % pointStyles.length],
          borderDash: [7, 5],
          pointRadius: 5,
          pointHoverRadius: 6,
          pointHitRadius: 6,
          order: index,
        };
      });
    }, [data, opponentColors, pointStyles]);

  const chartData: ChartData<"bar" | "line", number[]> = useMemo(() => {
    const { positive, negative } = theme.palette.data;
    const datasets: ChartDataset<"bar" | "line", number[]>[] = [
      {
        label: t("case.analytics.ev.chart.expectedValue"),
        data: data.map((item) => item.expectedValue),
        backgroundColor: data.map((party) =>
          alpha(
            party.expectedValue < 0 ? negative.main : positive.main,
            EXPECTED_VALUE_FILL_ALPHA,
          ),
        ),
        borderColor: data.map((party) =>
          party.expectedValue < 0 ? negative.dark : positive.dark,
        ),
        borderWidth: 1,
        pointStyle: LEGEND_BOX,
        stack: "combined",
      },
      // what is spent hangs from the line of zero, what is still to come
      // under it
      {
        label: t("case.analytics.ev.chart.incurredCost"),
        data: data.map((item) => -item.incurredCost),
        ...getColorForCosts("incurred", theme),
        pointStyle: LEGEND_BOX,
        stack: "combined",
      },
      {
        label: t("case.analytics.ev.chart.estimatedCost"),
        data: data.map((item) => -item.estimatedCost),
        ...getColorForCosts("estimated", theme),
        pointStyle: LEGEND_BOX,
        stack: "combined",
      },
      ...baselineMidpoints,
    ];
    if (netResult !== undefined)
      datasets.push({
        label: t("case.analytics.ev.chart.netResult"),
        data: data.map(() => netResult),
        type: "line",
        borderColor: theme.palette.warning.dark,
        borderWidth: 2,
        borderDash: [6, 6],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 8,
        spanGaps: true,
        order: 3,
      });
    return {
      labels: data.map((item) => item.label),
      datasets,
    };
  }, [data, baselineMidpoints, theme]);

  return (
    <Chart
      type="bar"
      data={chartData}
      options={{
        // the chart fills the frame it is given rather than keeping a shape
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            align: "start",
            labels: {
              ...getLegendLabels(theme),
              boxWidth: 12,
              boxHeight: 12,
              // a line is drawn as itself, with its marker
              usePointStyle: true,
              padding: 16,
            },
          },
          tooltip: {
            ...getTooltip(theme),
            callbacks: {
              label: ({ dataset, parsed }) =>
                `${dataset.label}: ${currency.format(parsed.y ?? 0)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              ...getTicks(theme),
              callback: (value) =>
                typeof value === "number"
                  ? currency.formatCompact(value)
                  : value,
            },
            grid: {
              // the line of zero stands out from the lines of the other amounts
              color: (context) =>
                context.tick.value === 0
                  ? theme.palette.text.disabled
                  : theme.palette.divider,
            },
          },
          x: {
            grid: { display: false },
            ticks: {
              ...getTicks(theme),
              maxRotation: 0,
              autoSkip: false,
              callback(value) {
                return wrapLabel(this.getLabelForValue(Number(value)));
              },
            },
          },
        },
      }}
    />
  );
};
